import { ALargeSmall } from 'lucide-react';
import { IconButton, Menu, MenuContent, MenuLabel, MenuRadioGroup, MenuRadioItem, MenuTrigger, Tooltip, useToast } from '@/components/ui';
import { describeError } from '@/lib/api';
import { FONT_SIZE_OPTIONS, useFontSizePreference, type FontSizePreference } from '@/lib/fontSize';
import { useSession } from '@/lib/session';
import './font-size-menu.css';

/** Glyph sizes for the segmented options — fixed px so the preview doesn't scale with itself. */
const GLYPH_PX: Record<FontSizePreference, number> = { small: 12, default: 16, large: 21 };

/**
 * Header control for the user's interface text size. It scales the root (html) font size, which
 * every rem-based token in the type scale and spacing follows; applies instantly and saves to the
 * account (and localStorage, so it holds across refresh and sign-out/sign-in).
 */
export function FontSizeMenu() {
  const session = useSession();
  const toast = useToast();
  const [value, setValue] = useFontSizePreference(!!session.user);
  const choose = (pref: FontSizePreference) => {
    if (pref === value) return;
    setValue(pref)
      .then(() => session.refresh())
      .catch((e: unknown) => toast.show({ tone: 'danger', title: 'Couldn’t save your text size', description: describeError(e).message }));
  };
  const current = FONT_SIZE_OPTIONS.find((o) => o.value === value);
  return (
    <Menu>
      <Tooltip content="Text size">
        <MenuTrigger asChild>
          <IconButton className="app-font-size" label="Text size" data-size={value} icon={<ALargeSmall size={20} />} />
        </MenuTrigger>
      </Tooltip>
      <MenuContent align="end" minWidth={260} className="font-size-menu">
        <MenuLabel>Text size</MenuLabel>
        <div className="fs-seg">
          <MenuRadioGroup value={value} onValueChange={(v) => choose(v as FontSizePreference)}>
            {FONT_SIZE_OPTIONS.map((o) => (
              <MenuRadioItem key={o.value} value={o.value}>
                <span className="fs-glyph" style={{ fontSize: GLYPH_PX[o.value] }} aria-hidden>
                  A
                </span>
                <span className="fs-label">{o.label}</span>
              </MenuRadioItem>
            ))}
          </MenuRadioGroup>
        </div>
        <p className="fs-hint">{current ? `${current.description}. ` : ''}Saved to your account on every device.</p>
      </MenuContent>
    </Menu>
  );
}
